<template>
  <DialogLayout class="flow-migrate-record-detail"
                title="流程迁移详情"
                v-loading="loading"
                element-loading-text="数据加载中，请稍等...">
    <template v-if="detailData">
      <div class="detail-main">
        <!-- 批次基本信息 -->
        <div class="detail-header">
          <div class="header-item">
            <span class="item-label">批次ID：</span>
            <span class="item-value">{{ detailData.batchId }}</span>
          </div>
          <div class="header-item">
            <span class="item-label">完成时间：</span>
            <span class="item-value">{{ detailData.completeTime || '-' }}</span>
          </div>
          <div class="header-item">
            <span class="item-label">状态：</span>
            <el-tag :type="detailData.completed ? 'success' : 'warning'" size="small">
              {{ detailData.completed ? '已完成' : '进行中' }}
            </el-tag>
          </div>
        </div>

        <!-- 迁移进度 -->
        <div class="progress-section">
          <div class="section-title">迁移进度</div>
          <div class="progress-content">
            <el-progress
              :text-inside="true"
              :stroke-width="20"
              :percentage="detailData.progressPercent"
              :status="detailData.failedProcessInstances > 0 ? 'warning' : (detailData.completed ? 'success' : undefined)">
            </el-progress>
            <div class="progress-stats">
              <div class="stat-item">
                <span class="stat-label">总数：</span>
                <span class="stat-value">{{ detailData.totalProcessInstances }}</span>
              </div>
              <div class="stat-item success">
                <span class="stat-label">成功：</span>
                <span class="stat-value">{{ detailData.migratedProcessInstances }}</span>
              </div>
              <div class="stat-item danger">
                <span class="stat-label">失败：</span>
                <span class="stat-value">{{ detailData.failedProcessInstances }}</span>
              </div>
              <div class="stat-item info">
                <span class="stat-label">等待：</span>
                <span class="stat-value">{{ detailData.waitingProcessInstances }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 流程定义信息 -->
        <div class="definition-section">
          <div class="section-title">流程定义</div>
          <div class="definition-content">
            <div class="definition-item">
              <span class="definition-label">源流程：</span>
              <div class="definition-detail">
                <span class="definition-name">{{ detailData.sourceProcessDefinitionName || '-' }}</span>
                <el-tag size="mini" v-if="detailData.sourceProcessDefinitionVersion">
                  第 {{ detailData.sourceProcessDefinitionVersion }} 版
                </el-tag>
                <span class="definition-id">ID: {{ detailData.sourceProcessDefinitionId }}</span>
              </div>
            </div>
            <div class="definition-arrow">
              <i class="el-icon-right"></i>
            </div>
            <div class="definition-item">
              <span class="definition-label">目标流程：</span>
              <div class="definition-detail">
                <span class="definition-name">{{ detailData.targetProcessDefinitionName || '-' }}</span>
                <el-tag size="mini" type="success" v-if="detailData.targetProcessDefinitionVersion">
                  第 {{ detailData.targetProcessDefinitionVersion }} 版
                </el-tag>
                <span class="definition-id">ID: {{ detailData.targetProcessDefinitionId }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 实例详情列表 -->
        <div class="instances-section">
          <div class="section-title">实例详情</div>
          <el-tabs v-model="activeTab" type="border-card">
            <!-- 全部 Tab -->
            <el-tab-pane :label="`全部 (${allDetails.length})`" name="all" lazy>
              <DataGrid
                height="400px"
                size="mini"
                border="inner"
                show-overflow="title"
                :data="allDetails">
                <DataGridColumn
                  field="processInstanceId"
                  title="流程实例ID"
                  min-width="200">
                </DataGridColumn>
                <DataGridColumn
                  field="status"
                  title="状态"
                  align="center"
                  width="100">
                  <template v-slot="{ row }">
                    <el-tag v-if="row.status === 'completed'" type="success" size="mini">已完成</el-tag>
                    <el-tag v-else-if="row.status === 'waiting'" type="info" size="mini">等待中</el-tag>
                    <el-tag v-else type="warning" size="mini">{{ row.status }}</el-tag>
                  </template>
                </DataGridColumn>
                <DataGridColumn
                  field="result"
                  title="结果"
                  align="center"
                  width="100">
                  <template v-slot="{ row }">
                    <span v-if="row.result === 'success'" class="result-success">成功</span>
                    <span v-else-if="row.result === 'failure'" class="result-failure">失败</span>
                    <span v-else>{{ row.result || '-' }}</span>
                  </template>
                </DataGridColumn>
                <DataGridColumn
                  field="migrationMessage"
                  title="迁移消息"
                  min-width="250">
                  <template v-slot="{ row }">
                    {{ row.migrationMessage || '-' }}
                  </template>
                </DataGridColumn>
                <span slot="empty">暂无数据</span>
              </DataGrid>
            </el-tab-pane>

            <!-- 成功 Tab -->
            <el-tab-pane :label="`成功 (${successfulDetails.length})`" name="success" lazy>
              <DataGrid
                height="400px"
                size="mini"
                border="inner"
                show-overflow="title"
                :data="successfulDetails">
                <DataGridColumn
                  field="processInstanceId"
                  title="流程实例ID"
                  min-width="200">
                </DataGridColumn>
                <DataGridColumn
                  field="status"
                  title="状态"
                  align="center"
                  width="100">
                  <template v-slot="{ row }">
                    <el-tag type="success" size="mini">已完成</el-tag>
                  </template>
                </DataGridColumn>
                <span slot="empty">暂无成功的实例</span>
              </DataGrid>
            </el-tab-pane>

            <!-- 失败 Tab -->
            <el-tab-pane :label="`失败 (${failedDetails.length})`" name="failed" lazy>
              <DataGrid
                height="400px"
                size="mini"
                border="inner"
                show-overflow="title"
                :data="failedDetails">
                <DataGridColumn
                  field="processInstanceId"
                  title="流程实例ID"
                  min-width="200">
                </DataGridColumn>
                <DataGridColumn
                  field="status"
                  title="状态"
                  align="center"
                  width="100">
                  <template v-slot="{ row }">
                    <el-tag type="danger" size="mini">已完成</el-tag>
                  </template>
                </DataGridColumn>
                <DataGridColumn
                  field="migrationMessage"
                  title="失败原因"
                  min-width="300">
                  <template v-slot="{ row }">
                    <span class="error-message">{{ row.migrationMessage || '未知错误' }}</span>
                  </template>
                </DataGridColumn>
                <span slot="empty">暂无失败的实例</span>
              </DataGrid>
            </el-tab-pane>

            <!-- 等待 Tab -->
            <el-tab-pane :label="`等待 (${waitingDetails.length})`" name="waiting" lazy>
              <DataGrid
                height="400px"
                size="mini"
                border="inner"
                show-overflow="title"
                :data="waitingDetails">
                <DataGridColumn
                  field="processInstanceId"
                  title="流程实例ID"
                  min-width="200">
                </DataGridColumn>
                <DataGridColumn
                  field="status"
                  title="状态"
                  align="center"
                  width="100">
                  <template v-slot="{ row }">
                    <el-tag type="info" size="mini">等待中</el-tag>
                  </template>
                </DataGridColumn>
                <span slot="empty">暂无等待的实例</span>
              </DataGrid>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </template>
    <template v-else-if="!loading">
      <Hint type="empty">
        <div>暂无迁移详情数据</div>
      </Hint>
    </template>
  </DialogLayout>
</template>

<script>
import DialogLayout from '../../../../../widgets/DialogLayout'
import DataGrid from '../../../../../elements/DataGrid'
import DataGridColumn from '../../../../../elements/DataGridColumn'
import Hint from '../../../../../elements/Hint'

export default {
  name: 'FlowMigrateRecordDetail',
  components: {
    DialogLayout,
    DataGrid,
    DataGridColumn,
    Hint
  },
  props: {
    rowData: {
      type: Object,
      required: true
    },
    visible: Boolean
  },
  data () {
    return {
      loading: false,
      detailData: null,
      activeTab: 'all',
      pollingTimer: null
    }
  },
  computed: {
    allDetails () {
      return (this.detailData && this.detailData.allDetails) || []
    },
    successfulDetails () {
      const allDetails = this.allDetails
      const successfulIds = (this.detailData && this.detailData.successfulIds) || []
      return allDetails.filter(detail => successfulIds.includes(detail.processInstanceId))
    },
    failedDetails () {
      const allDetails = this.allDetails
      const failedIds = (this.detailData && this.detailData.failedIds) || []
      return allDetails.filter(detail => failedIds.includes(detail.processInstanceId))
    },
    waitingDetails () {
      const allDetails = this.allDetails
      const waitingIds = (this.detailData && this.detailData.waitingIds) || []
      return allDetails.filter(detail => waitingIds.includes(detail.processInstanceId))
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.loadDetail()
      } else {
        this.stopPolling()
      }
    }
  },
  mounted () {
    this.loadDetail()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    loadDetail () {
      this.loading = true
      this.$ajax({
        url: '/workspace/manager/flow/configure/migrate/detail',
        method: 'post',
        data: this.$qs.stringify({
          batchId: this.rowData.batchId
        })
      }).then(response => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.detailData = response.data.result || null
              // 如果迁移未完成，启动轮询
              if (this.detailData && !this.detailData.completed) {
                this.startPolling()
              }
              break
            }
            default: {
              this.$message.error(response.data['message'] || '加载迁移详情失败')
              break
            }
          }
        }
      }).catch(() => {
        this.$message.error('加载迁移详情失败')
      }).finally(() => {
        this.loading = false
      })
    },
    startPolling () {
      // 每2秒轮询一次
      this.pollingTimer = setInterval(() => {
        this.loadDetail()
      }, 2000)
    },
    stopPolling () {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    }
  }
}
</script>

<style lang="scss">
.flow-migrate-record-detail {
  height: 100%;
  width: 760px;
  max-width: 100%;

  .detail-main {
    padding: 16px;
    .detail-header {
      display: flex;
      gap: 24px;
      padding: 16px;
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      margin-bottom: 16px;

      .header-item {
        display: flex;
        align-items: center;

        .item-label {
          font-size: 13px;
          color: #909399;
          white-space: nowrap;
          margin-right: 8px;
        }

        .item-value {
          font-size: 13px;
          color: #303133;
          font-weight: 500;
        }
      }
    }

    .progress-section,
    .definition-section,
    .instances-section {
      .section-title {
        font-size: 14px;
        font-weight: 500;
        color: #303133;
        margin-bottom: 12px;
        padding-left: 8px;
        border-left: 3px solid #409eff;
      }

      .el-tabs {
        &__content {
          padding: 0 10px 10px;
        }
      }
    }

    .progress-content {
      padding: 16px;
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      border-radius: 4px;

      .progress-stats {
        display: flex;
        gap: 24px;
        margin-top: 12px;
        font-size: 13px;

        .stat-item {
          display: flex;
          align-items: center;

          .stat-label {
            color: #606266;
            margin-right: 4px;
          }

          .stat-value {
            font-weight: 500;
            color: #303133;
          }

          &.success {
            .stat-value {
              color: #67c23a;
            }
          }

          &.danger {
            .stat-value {
              color: #f56c6c;
            }
          }

          &.info {
            .stat-value {
              color: #909399;
            }
          }
        }
      }
    }

    .definition-content {
      display: flex;
      align-items: center;
      padding: 16px;
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      border-radius: 4px;

      .definition-item {
        flex: 1;
        display: flex;
        flex-direction: column;

        .definition-label {
          font-size: 12px;
          color: #909399;
          margin-bottom: 8px;
        }

        .definition-detail {
          display: flex;
          flex-direction: column;
          gap: 6px;

          .definition-name {
            font-size: 14px;
            color: #303133;
            font-weight: 600;
          }

          .definition-id {
            font-size: 11px;
            color: #909399;
            font-family: monospace;
          }
        }

        .definition-value {
          font-size: 13px;
          color: #303133;
          font-weight: 500;
        }
      }

      .definition-arrow {
        flex: none;
        padding: 0 16px;
        font-size: 18px;
        color: #409eff;
      }
    }
  }

  .instances-section {
    .result-success {
      color: #67c23a;
      font-weight: 500;
    }

    .result-failure {
      color: #f56c6c;
      font-weight: 500;
    }

    .error-message {
      color: #f56c6c;
      font-size: 12px;
    }
  }
}
</style>
